import { ContentLink, usePageContent } from "@robe/client/xcore-ui";
import { Box, Flex, Icon, Img, Typography, Link } from "@xcorejs/ui";
import BurgerIcon from "components/Icons/24/burger.svg";
import CloseIcon from "components/Icons/20/close.svg";
import SearchIcon from "components/Icons/20/search.svg";
import User from "components/Icons/user.svg";
import Navigation from "design-system/appio/navigation";
import { Router, useRouter } from "next/router";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useLayout } from "xcore";
import { useUser } from "hooks/useUser";
import DomainSwitcher from "./DomainSwitcher";
import { HeaderMiddleContent } from "./HeaderMiddleContent";
import MobileDomainSwitcher from "./MobileDomainSwitcher";
import HeaderSearchField from "./HeaderSearchField";
import { RegionInfo } from "./RegionInfo";

export type HeaderState = "menu" | "search" | "localeSwitcher" | null;

const Header = () => {
  const { user } = useUser();
  const isLogged = !!user;

  const [state, setState] = useState<HeaderState>(null);
  const closeSearch = useCallback(() => setState(null), []);
  const [closeRegionInfo, setCloseRegionInfo] = useState(false);
  const [isTop, setIsTop] = useState(true);

  const content = usePageContent();
  const {
    header: { nav },
    config,
  } = useLayout();
  const { isPreview } = useRouter();
  const ref = useRef<HTMLAnchorElement>(null);

  let env = null;
  switch (config.env) {
    case "local":
      env = "Local (xcc)";
      break;
    case "dev":
      env = "Dev (xcc)";
      break;
    case "test":
      env = "Test (xcc)";
      break;
    default:
      break;
  }

  useEffect(() => {
    document.documentElement.style.setProperty("--vh", window.innerHeight * 0.01 + "px");

    const h2 = () => document.documentElement.style.setProperty("--vh", window.innerHeight * 0.01 + "px");
    window.addEventListener("resize", h2);

    const h1 = () => setState(null);
    Router.events.on("routeChangeComplete", h1);

    const handleScroll = () => {
      setIsTop(window.scrollY <= 20); // 2rem = 20px
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check on mount

    return () => {
      Router.events.off("routeChangeComplete", h1);
      window.removeEventListener("resize", h2);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!closeRegionInfo && state) {
      setCloseRegionInfo(true);
    }
  }, [closeRegionInfo, state]);

  return (
    <>
      <MobileDomainSwitcher
        mode="bar"
        isLogged={isLogged}
        open={isTop && state === "localeSwitcher"}
        setState={(o) => setState(o ? "localeSwitcher" : null)}
      />
      <Navigation
        leftContent={
          <Flex>
            <ContentLink
              content={nav.homepage}
              type="simple"
              width={{ _: "10rem", xxs: "10.3rem", md: "12rem", lg: "14rem" }}
              alignItems="center"
              flex="0 0 auto"
            >
              <Img src="/static/robe-black.svg" alt="home" width="100%" />
              {env && (
                <Typography m="4rem 0 0 .4rem" fontSize="14px" whiteSpace="nowrap">
                  {env}
                </Typography>
              )}
            </ContentLink>
            <Box display={{ _: "flex", md: "none" }} m="4.5rem 0 0 -13.7rem">
              <RegionInfo isTop={isTop} close={closeRegionInfo} setClose={setCloseRegionInfo} />
            </Box>
          </Flex>
        }
        middleContent={<HeaderMiddleContent state={state} setState={setState} />}
        rightContent={
          <Flex justifyContent="flex-end" alignItems="center" gridColumn={{ _: "2/4", md: "3/4" }}>
            {!isTop && (
              <MobileDomainSwitcher mode="icon" open={state === "localeSwitcher"} setState={(o) => setState(o ? "localeSwitcher" : null)} />
            )}
            <Link
              type="simple"
              ref={ref}
              onClick={() => setTimeout(() => setState(state === "search" ? null : "search"), 50)} // timeout because of race condition of setState
              pl={{ _: "1rem", md: 0 }}
              alignItems="center"
            >
              <Icon
                svg={state === "search" ? <CloseIcon /> : <SearchIcon />}
                fill={content.type === "search" ? "robe" : "gray"}
                fillHover="black"
              />
            </Link>

            <Flex flexDirection="column" position="relative">
              <DomainSwitcher />
              <RegionInfo close={closeRegionInfo} setClose={setCloseRegionInfo} display={{ _: "none", md: "flex" }} />
            </Flex>

            <Box onClick={() => setState((s) => (s === "menu" ? null : "menu"))}>
              {isLogged && state !== "menu" && (
                <Box
                  display={{ _: "flex", sm: "none" }}
                  justifyContent="center"
                  alignItems="center"
                  position="absolute"
                  border="none"
                  top={12}
                  right={9}
                >
                  <User />
                </Box>
              )}
              <Icon
                svg={state === "menu" ? <CloseIcon /> : <BurgerIcon />}
                fill="robe"
                ml={{ _: "1.8rem", lg: "2rem" }}
                display={{ _: "block", md: "none" }}
              />
            </Box>
            <HeaderSearchField open={state === "search"} close={closeSearch} />
          </Flex>
        }
        _container={{ justifyContent: "space-between", alignItems: "center", position: state === "search" ? "relative" : "static" }}
        _grid={{ columns: { _: "1fr auto 1fr", md: "13rem 1fr auto", lg: "17rem 1fr auto" }, rows: "auto" }}
        position="sticky"
        top={{ _: 0, sm: `${3.2 * (+isLogged + +isPreview)}rem` }}
        zIndex={1003}
        height={{ _: "5rem", md: "8.4rem" }}
        {...(!isTop && { boxShadow: { _: "0 0 1rem 0 rgba(30, 30, 30, 0.25)", md: "none" } })}
        borderBottom={{ _: !state ? "1px solid lightGray" : "none", md: "none" }}
      />
    </>
  );
};

export default memo(Header);
